import React, { useEffect, useMemo, useState } from 'react'
import { FaMusic, FaPalette, FaQrcode, FaStar } from 'react-icons/fa'
import { BASE_URL_Upload } from '../Component/commonComponents/ApiEndpoints'
import { useSidebar } from '../Component/SidebarContext'

const REGISTRY_TITLE = 'Cultural Activity Certificate'

const formatDdMmYyyy = (value) => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(value.trim())) {
    return value.trim()
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) {
    return typeof value === 'string' ? value : ''
  }
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}-${month}-${year}`
}

const safeStr = (v, fallback = '') => {
  if (v === null || v === undefined) return fallback
  const s = String(v).trim()
  if (!s || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return fallback
  return s
}

const resolveMediaUrl = (src) => {
  const t = safeStr(src, '')
  if (!t) return ''
  if (/^https?:\/\//i.test(t)) return t
  if (t.startsWith('//')) return `https:${t}`
  const base = String(BASE_URL_Upload || '').replace(/\/$/, '')
  const path = t.startsWith('/') ? t : `/${t}`
  return base ? `${base}${path}` : t
}

const displayTitleFromProps = (certificateTitle) => {
  const t = safeStr(certificateTitle, '')
  if (!t || t === REGISTRY_TITLE) return 'Cultural Activity Certificate'
  return t
}

const deriveAchievement = (certificate) => {
  const raw = safeStr(
    certificate?.position ??
      certificate?.achievement ??
      certificate?.event_position ??
      certificate?.result,
    '',
  )
  if (raw) return raw
  return 'Participant'
}

const deriveMention = (certificate, grade) => {
  const raw = safeStr(
    certificate?.special_mention ?? certificate?.mention ?? certificate?.performance_tag,
    '',
  )
  if (raw) return raw
  if (String(grade || '').toLowerCase().includes('a')) return 'Outstanding Performance'
  return 'Excellent Participation'
}

const deriveActivityTheme = (activityName = '') => {
  const a = activityName.toLowerCase()
  if (a.includes('music') || a.includes('sing')) return 'music'
  if (a.includes('dance')) return 'dance'
  if (a.includes('art') || a.includes('paint')) return 'art'
  if (a.includes('drama') || a.includes('theatre')) return 'drama'
  return 'general'
}

const CertificateTemplate7 = ({
  studentName = 'Student Name',
  instituteName = 'Institute Name',
  date = '',
  certificateId = '',
  certificateTitle = 'Cultural Activity Certificate',
  logoUrl = '',
  activity_name,
  student: studentProp,
  course: courseProp,
  certificate: certificateProp,
  institute: instituteProp,
  selectedStudent,
  selectedCourse,
  suppressSidebarOffset = false,
}) => {
  const certificate = certificateProp || {}
  const student = studentProp || selectedStudent || {}
  const course = courseProp || selectedCourse || {}
  const institute = instituteProp || {}

  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [photoFailed, setPhotoFailed] = useState(false)
  const [fallbackPhotoFailed, setFallbackPhotoFailed] = useState(false)

  useEffect(() => {
    if (suppressSidebarOffset) return undefined
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed, suppressSidebarOffset])

  useEffect(() => {
    setPhotoFailed(false)
    setFallbackPhotoFailed(false)
  }, [student?.student_image, student?.photo, student?.image])

  const data = useMemo(() => {
    const instName =
      safeStr(institute.institute_name) ||
      safeStr(institute.name) ||
      safeStr(instituteName)

    const studName = safeStr(student.name) || safeStr(studentName)
    const father = safeStr(student.father_name)
    const mother = safeStr(student.mother_name)
    const admission = safeStr(student.admission_number)

    const activityName =
      safeStr(activity_name) ||
      safeStr(certificate.activity_name) ||
      safeStr(course.activity_name) ||
      safeStr(course.event_name) ||
      'Cultural Activity'

    const eventName =
      safeStr(certificate.event_name) ||
      safeStr(course.event_name) ||
      safeStr(certificate.program_name)

    const start = formatDdMmYyyy(course.start_date ?? course.startDate)
    const end = formatDdMmYyyy(course.end_date ?? course.endDate)

    const certNum =
      safeStr(certificate.certificate_number) ||
      safeStr(certificate.certificate_id) ||
      safeStr(certificate.id != null ? String(certificate.id) : '') ||
      safeStr(certificateId)

    const issueRaw =
      certificate.issue_date ?? certificate.date ?? certificate.issueDate ?? date
    const issue = formatDdMmYyyy(issueRaw) || (typeof issueRaw === 'string' ? issueRaw : '')

    const grade = safeStr(certificate.grade)
    const achievement = deriveAchievement(certificate)
    const mention = deriveMention(certificate, grade)

    const imgRaw =
      student.student_image ?? student.photo ?? student.image ?? student.profile_image
    const photoSrc = resolveMediaUrl(imgRaw)

    const titlePrinted = displayTitleFromProps(certificateTitle)
    const theme = deriveActivityTheme(activityName)

    return {
      instName,
      studName,
      father,
      mother,
      admission,
      activityName,
      eventName,
      start,
      end,
      certNum,
      issue,
      grade,
      achievement,
      mention,
      photoSrc,
      titlePrinted,
      theme,
    }
  }, [
    certificate,
    student,
    course,
    institute,
    studentName,
    instituteName,
    certificateId,
    certificateTitle,
    date,
    activity_name,
  ])

  const {
    instName,
    studName,
    father,
    mother,
    admission,
    activityName,
    eventName,
    start,
    end,
    certNum,
    issue,
    grade,
    achievement,
    mention,
    photoSrc,
    titlePrinted,
    theme,
  } = data

  const eventPeriod =
    start && end ? `${start} to ${end}` : start || end ? `${start || '—'} to ${end || '—'}` : ''

  const qrData = useMemo(() => {
    const parts = [instName, certNum || 'PREVIEW', issue || formatDdMmYyyy(new Date())].filter(
      Boolean,
    )
    return parts.join('|')
  }, [instName, certNum, issue])

  const qrSrc =
    typeof window !== 'undefined'
      ? `https://api.qrserver.com/v1/create-qr-code/?size=72x72&margin=0&data=${encodeURIComponent(
          qrData,
        )}`
      : ''

  const defaultPhoto = `${process.env.PUBLIC_URL || ''}/studentprofile.jpg`
  const showPhoto = photoSrc && !photoFailed
  const logoResolved = resolveMediaUrl(logoUrl) || logoUrl
  const layoutMarginLeft = suppressSidebarOffset ? '0px' : marginLeft

  const handlePrint = () => {
    window.print()
  }

  return (
    <div
      className="certificate-template-7-page"
      data-theme={theme}
      style={{
        marginLeft: layoutMarginLeft,
        transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        willChange: 'margin-left',
        WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Poppins:wght@400;500;600;700&display=swap');

          .certificate-template-7-page {
            min-height: 100vh;
            background: linear-gradient(160deg, #f3e8ff 0%, #fff7ed 45%, #fff1d6 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
            box-sizing: border-box;
          }

          .certificate-template-7-wrap {
            width: min(297mm, 100%);
          }

          .certificate-template-7-actions {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 12px;
          }

          .certificate-template-7-print-btn {
            border: none;
            background: linear-gradient(135deg, #7c3aed, #f97316);
            color: #fff;
            padding: 10px 18px;
            border-radius: 10px;
            font-family: 'Poppins', sans-serif;
            font-weight: 700;
            font-size: 13px;
            cursor: pointer;
            box-shadow: 0 8px 18px rgba(124, 58, 237, 0.2);
          }

          .certificate-template-7-card {
            width: 100%;
            aspect-ratio: 297 / 210;
            max-height: min(90vh, 210mm);
            margin: 0 auto;
            background: #fff;
            position: relative;
            overflow: hidden;
            border-radius: 8px;
            box-shadow: 0 18px 44px rgba(15, 23, 42, 0.12);
            border: 1px solid #f2d3aa;
          }

          .certificate-template-7-card::before {
            content: '';
            position: absolute;
            inset: 0;
            border: 4px solid transparent;
            border-image: linear-gradient(135deg, #7c3aed, #f97316, #fbbf24) 1;
            pointer-events: none;
            z-index: 2;
          }

          .certificate-template-7-card::after {
            content: '';
            position: absolute;
            inset: 12px;
            border: 1px solid rgba(124, 58, 237, 0.25);
            pointer-events: none;
            z-index: 1;
          }

          .certificate-template-7-pattern {
            position: absolute;
            inset: 0;
            opacity: 0.08;
            background-image:
              radial-gradient(circle at 12% 22%, #7c3aed 0 2px, transparent 3px),
              radial-gradient(circle at 82% 18%, #f97316 0 2px, transparent 3px),
              radial-gradient(circle at 66% 76%, #f59e0b 0 2px, transparent 3px),
              radial-gradient(circle at 26% 80%, #ec4899 0 2px, transparent 3px);
            background-size: 120px 120px;
            pointer-events: none;
            z-index: 0;
          }

          .certificate-template-7-watermark {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'Playfair Display', serif;
            font-size: clamp(52px, 13vw, 108px);
            color: rgba(124, 58, 237, 0.07);
            letter-spacing: 0.12em;
            text-transform: uppercase;
            pointer-events: none;
            z-index: 0;
          }

          .certificate-template-7-inner {
            position: relative;
            z-index: 3;
            height: 100%;
            box-sizing: border-box;
            padding: clamp(14px, 2vw, 22px) clamp(18px, 2.8vw, 30px) 16px;
            display: flex;
            flex-direction: column;
            min-height: 0;
          }

          .certificate-template-7-head {
            text-align: center;
            flex-shrink: 0;
            position: relative;
            min-height: 72px;
          }

          .certificate-template-7-logo {
            width: 52px;
            height: 52px;
            margin: 0 auto 6px;
            object-fit: cover;
            border-radius: 50%;
            border: 2px solid #7c3aed;
            background: #fff;
          }

          .certificate-template-7-logo-ph {
            width: 52px;
            height: 52px;
            margin: 0 auto 6px;
            border-radius: 50%;
            border: 2px dashed #c4b5fd;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            color: #a78bfa;
            font-family: 'Poppins', sans-serif;
          }

          .certificate-template-7-photo {
            position: absolute;
            right: 0;
            top: 0;
            width: 78px;
            height: 96px;
            border: 2px solid #f59e0b;
            border-radius: 8px;
            overflow: hidden;
            background: #fff;
          }

          .certificate-template-7-photo img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .certificate-template-7-photo-ph {
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            color: #94a3b8;
            text-align: center;
            padding: 4px;
          }

          .certificate-template-7-institute {
            margin: 0;
            font-family: 'Poppins', sans-serif;
            font-size: clamp(12px, 1.4vw, 14px);
            font-weight: 700;
            letter-spacing: 0.07em;
            text-transform: uppercase;
            color: #7c3aed;
          }

          .certificate-template-7-title {
            margin: 8px 0 0;
            font-family: 'Playfair Display', serif;
            font-size: clamp(22px, 3vw, 32px);
            font-weight: 700;
            color: #111827;
            text-align: center;
            line-height: 1.2;
          }

          .certificate-template-7-title-accent {
            width: min(290px, 74%);
            height: 3px;
            margin: 8px auto 0;
            background: linear-gradient(90deg, transparent, #f97316, transparent);
            border-radius: 99px;
          }

          .certificate-template-7-ribbon {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            margin-top: 8px;
            padding: 4px 12px;
            border-radius: 999px;
            background: linear-gradient(135deg, #f59e0b, #f97316);
            color: #fff;
            font-size: 10px;
            font-weight: 700;
            font-family: 'Poppins', sans-serif;
            text-transform: uppercase;
            letter-spacing: 0.06em;
          }

          .certificate-template-7-main {
            margin-top: 10px;
            display: grid;
            grid-template-columns: 1fr min(116px, 22%);
            gap: 12px 16px;
            align-items: start;
            flex: 1;
            min-height: 0;
          }

          .certificate-template-7-body {
            font-family: 'Poppins', sans-serif;
            font-size: clamp(11px, 1.15vw, 13px);
            line-height: 1.65;
            color: #4b5563;
            text-align: left;
          }

          .certificate-template-7-body p {
            margin: 0 0 10px;
          }

          .certificate-template-7-name {
            font-weight: 700;
            font-size: clamp(12px, 1.25vw, 14px);
            color: #111827;
          }

          .certificate-template-7-em {
            font-weight: 600;
            color: #1f2937;
          }

          .certificate-template-7-divider {
            height: 1px;
            background: linear-gradient(90deg, transparent, #fde68a, transparent);
            margin: 8px 0;
          }

          .certificate-template-7-details {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 8px;
          }

          .certificate-template-7-detail {
            padding: 8px;
            border: 1px solid #f3e8ff;
            border-radius: 8px;
            background: linear-gradient(180deg, #fff7ed 0%, #faf5ff 100%);
          }

          .certificate-template-7-label {
            font-size: 8px;
            color: #7c3aed;
            font-family: 'Poppins', sans-serif;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            font-weight: 700;
          }

          .certificate-template-7-value {
            margin-top: 2px;
            font-size: 10px;
            font-weight: 600;
            color: #111827;
            word-break: break-word;
          }

          .certificate-template-7-recognition {
            margin-top: 8px;
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 8px;
          }

          .certificate-template-7-meta {
            margin-top: 8px;
            display: flex;
            flex-wrap: wrap;
            gap: 10px 16px;
            font-size: 10px;
            color: #4b5563;
            font-family: 'Poppins', sans-serif;
          }

          .certificate-template-7-meta strong {
            color: #111827;
          }

          .certificate-template-7-side {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .certificate-template-7-qr img {
            width: 58px;
            height: 58px;
            display: block;
          }

          .certificate-template-7-qr-cap {
            font-size: 7px;
            color: #64748b;
            text-align: center;
            margin-top: 2px;
          }

          .certificate-template-7-icons {
            display: flex;
            gap: 6px;
            color: #f59e0b;
            font-size: 12px;
          }

          .certificate-template-7-footer {
            margin-top: auto;
            padding-top: 10px;
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 10px;
            flex-shrink: 0;
          }

          .certificate-template-7-sign {
            text-align: center;
            font-family: 'Poppins', sans-serif;
          }

          .certificate-template-7-seal {
            width: 42px;
            height: 42px;
            margin: 0 auto 4px;
            border: 2px solid #f59e0b;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #f59e0b;
            background: #fff7ed;
          }

          .certificate-template-7-sign-line {
            border-top: 1px solid #374151;
            margin-top: 22px;
            padding-top: 5px;
            font-size: 8px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: #111827;
          }

          .certificate-template-7-sign-sub {
            font-size: 8px;
            color: #6b7280;
            margin-top: 2px;
          }

          .certificate-template-7-page[data-theme='music'] .certificate-template-7-card::before {
            border-image: linear-gradient(135deg, #7c3aed, #6366f1, #22c55e) 1;
          }

          .certificate-template-7-page[data-theme='dance'] .certificate-template-7-card::before {
            border-image: linear-gradient(135deg, #ec4899, #f97316, #f59e0b) 1;
          }

          .certificate-template-7-page[data-theme='art'] .certificate-template-7-card::before {
            border-image: linear-gradient(135deg, #06b6d4, #7c3aed, #f59e0b) 1;
          }

          @media (max-width: 720px) {
            .certificate-template-7-main {
              grid-template-columns: 1fr;
            }
            .certificate-template-7-photo {
              position: relative;
              margin: 0 auto 8px;
            }
            .certificate-template-7-details,
            .certificate-template-7-recognition {
              grid-template-columns: 1fr;
            }
            .certificate-template-7-footer {
              grid-template-columns: 1fr;
            }
          }

          @media print {
            @page {
              size: A4 landscape;
              margin: 10mm;
            }
            body * {
              visibility: hidden;
            }
            .certificate-template-7-page,
            .certificate-template-7-page * {
              visibility: visible;
            }
            .certificate-template-7-page {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              background: #fff;
              padding: 0;
              min-height: auto;
              margin-left: 0 !important;
            }
            .certificate-template-7-actions,
            .certificate-template-7-actions * {
              display: none !important;
              visibility: hidden !important;
            }
            .certificate-template-7-wrap {
              width: 100%;
            }
            .certificate-template-7-card {
              max-height: none;
              box-shadow: none;
              border-radius: 0;
              aspect-ratio: auto;
              min-height: 185mm;
            }
          }
        `}
      </style>

      <div className="certificate-template-7-wrap">
        <div className="certificate-template-7-actions">
          <button type="button" className="certificate-template-7-print-btn" onClick={handlePrint}>
            Print / Download
          </button>
        </div>

        <article className="certificate-template-7-card">
          <div className="certificate-template-7-pattern" aria-hidden />
          <div className="certificate-template-7-watermark" aria-hidden>
            Cultural
          </div>

          <div className="certificate-template-7-inner">
            <header className="certificate-template-7-head">
              {logoResolved ? (
                <img src={logoResolved} alt="" className="certificate-template-7-logo" />
              ) : (
                <div className="certificate-template-7-logo-ph">Logo</div>
              )}
              <div className="certificate-template-7-photo" aria-label="Student photo">
                {showPhoto ? (
                  <img src={photoSrc} alt="" onError={() => setPhotoFailed(true)} />
                ) : !fallbackPhotoFailed ? (
                  <img
                    src={defaultPhoto}
                    alt=""
                    onError={() => setFallbackPhotoFailed(true)}
                  />
                ) : (
                  <div className="certificate-template-7-photo-ph">Photo</div>
                )}
              </div>

              <p className="certificate-template-7-institute">{instName || 'Institute Name'}</p>
              <h1 className="certificate-template-7-title">{titlePrinted}</h1>
              <div className="certificate-template-7-title-accent" />
              <span className="certificate-template-7-ribbon">
                <FaStar aria-hidden /> Star Performer
              </span>
            </header>

            <main className="certificate-template-7-main">
              <section className="certificate-template-7-body">
                <p>
                  This is to proudly certify that{' '}
                  <span className="certificate-template-7-name">{studName || '—'}</span>
                  {father || mother ? (
                    <>
                      ,<br />
                      {father ? (
                        <>
                          S/o <span className="certificate-template-7-em">{father}</span>
                          {mother ? ' and ' : ''}
                        </>
                      ) : null}
                      {mother ? (
                        <>
                          M/o <span className="certificate-template-7-em">{mother}</span>
                        </>
                      ) : null}
                      ,
                    </>
                  ) : admission ? (
                    ','
                  ) : null}
                  {admission ? (
                    <>
                      <br />
                      bearing Admission No. <span className="certificate-template-7-em">{admission}</span>,
                    </>
                  ) : null}
                  <br />
                  has actively participated in the cultural activity{' '}
                  <span className="certificate-template-7-em">{activityName}</span>.
                </p>

                <p>
                  The event was conducted during the period from{' '}
                  <span className="certificate-template-7-em">{start || '—'}</span> to{' '}
                  <span className="certificate-template-7-em">{end || '—'}</span>, and the student displayed
                  remarkable enthusiasm and talent.
                </p>

                <div className="certificate-template-7-divider" />

                <div className="certificate-template-7-details">
                  <div className="certificate-template-7-detail">
                    <div className="certificate-template-7-label">Activity Name</div>
                    <div className="certificate-template-7-value">{activityName || '—'}</div>
                  </div>
                  <div className="certificate-template-7-detail">
                    <div className="certificate-template-7-label">Event Date / Duration</div>
                    <div className="certificate-template-7-value">{eventPeriod || '—'}</div>
                  </div>
                  <div className="certificate-template-7-detail">
                    <div className="certificate-template-7-label">Position / Achievement</div>
                    <div className="certificate-template-7-value">{achievement || '—'}</div>
                  </div>
                </div>

                <div className="certificate-template-7-recognition">
                  <div className="certificate-template-7-detail">
                    <div className="certificate-template-7-label">Special Mention</div>
                    <div className="certificate-template-7-value">{mention || '—'}</div>
                  </div>
                  <div className="certificate-template-7-detail">
                    <div className="certificate-template-7-label">Grade / Score</div>
                    <div className="certificate-template-7-value">{grade || '—'}</div>
                  </div>
                </div>

                <div className="certificate-template-7-meta">
                  <span>
                    <strong>Certificate No:</strong> {certNum || '—'}
                  </span>
                  <span>
                    <strong>Issue Date:</strong> {issue || '—'}
                  </span>
                  {eventName ? (
                    <span>
                      <strong>Event Name:</strong> {eventName}
                    </span>
                  ) : null}
                </div>
              </section>

              <aside className="certificate-template-7-side">
                <div className="certificate-template-7-qr">
                  <img src={qrSrc} alt="" width={58} height={58} />
                  <div className="certificate-template-7-qr-cap">Verification</div>
                </div>
                <div className="certificate-template-7-icons" aria-hidden>
                  <FaMusic />
                  <FaPalette />
                  <FaQrcode />
                </div>
              </aside>
            </main>

            <footer className="certificate-template-7-footer">
              <div className="certificate-template-7-sign">
                <div className="certificate-template-7-sign-line">Event Coordinator</div>
                <div className="certificate-template-7-sign-sub">Cultural Event Team</div>
              </div>
              <div className="certificate-template-7-sign">
                <div className="certificate-template-7-sign-line">Cultural Head</div>
                <div className="certificate-template-7-sign-sub">Department Head</div>
              </div>
              <div className="certificate-template-7-sign">
                <div className="certificate-template-7-sign-line">Principal / Director</div>
                <div className="certificate-template-7-sign-sub">Institute Authority</div>
              </div>
              <div className="certificate-template-7-sign">
                <div className="certificate-template-7-seal">Seal</div>
                <div className="certificate-template-7-sign-line">Official Seal</div>
                <div className="certificate-template-7-sign-sub">Optional</div>
              </div>
            </footer>
          </div>
        </article>
      </div>
    </div>
  )
}

export default CertificateTemplate7
